import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, CalendarClock, ClipboardEdit, Sparkles, MapPin, CheckCircle2, User, ChevronRight,
  AlertTriangle, Link2,
} from "lucide-react";
import AIBriefingModal from "@/components/AIBriefingModal";
import MedicalRecordDialog from "./branch/calendar/MedicalRecordDialog";
import { colorForAppt } from "./branch/calendar/constants";
import { subscribe as liveSubscribe, ensureOpen as liveEnsureOpen } from "@/lib/liveSync";
import {
  prettyDate,
  groupAppointmentsByClient,
  buildMedicalRecordButton,
  isNewGuestGroup,
  deriveClientDayColor,
  filterApptsByBranch,
  branchNameMap,
} from "@/lib/RosterEngine";
import { resolveBranchTheme } from "@/lib/branchTheme";

/**
 * Care Team Dispatch — per-day worklist for a personal-view practitioner.
 * Consumes `/api/staff/my-daily-worklist` which respects the caller's
 * personal-view scope on the backend. Each row is a client with two
 * fat quick actions: AI Summary + Input Medical Record.
 */
export default function MyRosterDay() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const dateStr = params.get("date");
  const primaryBranchId = params.get("branch_id");
  const allBranchesCsv = params.get("all_branches") || primaryBranchId || "";

  const branchIds = useMemo(
    () => allBranchesCsv.split(",").map((s) => s.trim()).filter(Boolean),
    [allBranchesCsv]
  );
  const [selectedBranch, setSelectedBranch] = useState(primaryBranchId);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState(null);        // { customerId, customerName, serviceId, serviceName }
  const [medical, setMedical] = useState(null);          // { id, customer_name, service_name, start_iso }
  const [lockInfo, setLockInfo] = useState(null);        // { locked, oldest_incomplete_date, incomplete_count }

  const isTodayOrFuture = useMemo(() => {
    if (!dateStr) return false;
    const now = new Date();
    const [y, m, d] = dateStr.split("-").map(Number);
    const target = new Date(y, m - 1, d);
    target.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return target.getTime() >= now.getTime();
  }, [dateStr]);

  const hardStop = !!(lockInfo?.locked && isTodayOrFuture);

  const load = useCallback(async () => {
    if (!dateStr) return;
    setLoading(true);
    try {
      // Always load unfiltered so the response carries branch names for
      // BOTH sides of a multi-branch day; we filter client-side by
      // `selectedBranch` when rendering the list.
      const r = await staffApi.get("/staff/my-daily-worklist", { params: { date: dateStr } });
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
      setData({ appointments: [] });
    } finally {
      setLoading(false);
    }
  }, [dateStr]);

  useEffect(() => { load(); }, [load]);

  // Real-time visual sync (Feb 2026) — the personal roster tiles
  // must repaint the instant the Branch Calendar changes: a Front
  // Desk check-in flips the tile teal, a deposit lands green, an
  // order close paints it dark grey. Debounced refetch avoids
  // thrashing when a package wizard fires a burst of writes.
  useEffect(() => {
    liveEnsureOpen();
    let t = null;
    const bump = () => {
      if (t) return;
      t = setTimeout(() => { t = null; load(); }, 180);
    };
    const offA = liveSubscribe("appointment_updated", bump);
    const offC = liveSubscribe("customer_updated", bump);
    const offO = liveSubscribe("order_updated", bump);
    const onCustEvent = () => bump();
    window.addEventListener("cocoon:customer-updated", onCustEvent);
    return () => {
      offA(); offC(); offO();
      window.removeEventListener("cocoon:customer-updated", onCustEvent);
      if (t) clearTimeout(t);
    };
  }, [load]);

  const loadLock = useCallback(async () => {
    try {
      const r = await staffApi.get("/staff/my-medical-lock");
      setLockInfo(r.data);
    } catch {
      setLockInfo({ locked: false });
    }
  }, []);

  useEffect(() => { loadLock(); }, [loadLock]);

  const goBackToRoster = () => nav("/staff/my-roster");

  const branchName = useMemo(() => {
    const appts = data?.appointments || [];
    if (!appts.length) return "";
    return appts.find((a) => a.branch_id === selectedBranch)?.branch_name || "";
  }, [data, selectedBranch]);

  // Client-side filter so a multi-branch day can switch tabs instantly
  // without a fresh network round trip. Delegated to RosterEngine so
  // the same rule is guaranteed everywhere.
  const visibleAppts = useMemo(
    () => filterApptsByBranch(data?.appointments || [], selectedBranch),
    [data, selectedBranch],
  );

  // Client-centric grouping: One Client = One Card. Delegated to
  // RosterEngine.groupAppointmentsByClient so the STRICT rule holds —
  // walk-ins never merge under the shared "Guest" label, and Group
  // Bookings with different clients stay as separate cards.
  const clientGroups = useMemo(() => groupAppointmentsByClient(visibleAppts), [visibleAppts]);

  // Branch-name lookup for multi-branch conflict-day tabs.
  const branchNameById = useMemo(() => branchNameMap(data?.appointments || []), [data]);

  const openBriefing = (appt) => {
    if (!appt?.customer_id) { toast.error("This slot has no linked client."); return; }
    setBriefing({
      customerId: appt.customer_id,
      customerName: appt.customer_name || "Guest",
      serviceId: appt.service_id || null,
      serviceName: appt.service_name || "",
      appointmentId: appt.id || null,
    });
  };

  const openMedical = (appt) => {
    if (!appt?.id) return;
    setMedical(appt);
  };

  return (
    <div className="min-h-screen bg-[hsl(40_20%_97%)]">
      <header className="sticky top-0 z-10 h-14 px-3 sm:px-6 border-b border-border bg-white/95 backdrop-blur flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={goBackToRoster} data-testid="myroster-day-back">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Roster
          </Button>
          <div className="min-w-0">
            <p className="text-[11px] tracking-overline text-muted-foreground">DAILY WORKLIST</p>
            <p className="text-sm font-semibold truncate" data-testid="myroster-day-title">{prettyDate(dateStr)}</p>
          </div>
        </div>
        {/* Feb 2026: Calendar shortcut hidden from Care Team members —
            they are restricted to My Roster / Clients / My Performance
            (see `CareTeamTabs.jsx`). Kept the header slot as an empty
            spacer so the layout stays balanced without the button. */}
      </header>

      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-5">
        {/* Multi-branch conflict day: show branch tabs so the practitioner
            can flip between them without going back. */}
        {branchIds.length > 1 && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-3" data-testid="myroster-day-conflict-banner">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700 uppercase tracking-wider mb-2">
              <AlertTriangle className="w-3.5 h-3.5" />
              Multi-branch day
            </div>
            <div className="flex flex-wrap gap-2">
              {branchIds.map((bid) => (
                <button
                  key={bid}
                  type="button"
                  onClick={() => setSelectedBranch(bid)}
                  data-testid={`myroster-day-branch-tab-${bid}`}
                  className={[
                    "text-xs px-3 py-1.5 rounded-full border transition-colors",
                    bid === selectedBranch
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-red-800 border-red-300 hover:bg-red-100",
                  ].join(" ")}
                >
                  <MapPin className="inline w-3 h-3 mr-1 -mt-0.5" />
                  {(data?.appointments || []).find((a) => a.branch_id === bid)?.branch_name || branchNameById[bid] || bid.slice(0, 8)}
                </button>
              ))}
            </div>
          </div>
        )}

        {branchName && (
          <div className="mb-4 text-sm text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            <span>Working at <span className="font-medium text-foreground">{branchName}</span></span>
          </div>
        )}

        {hardStop && (
          <div
            className="mb-4 rounded-xl border-2 border-red-400 bg-red-50 p-3 flex items-start gap-3"
            role="alert"
            data-testid="myrosterday-lock-banner"
          >
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="min-w-0 text-xs leading-relaxed text-red-800">
              <p className="font-semibold">Account Locked</p>
              <p>Please complete your missing medical reports from <b>{lockInfo?.oldest_incomplete_date || "yesterday"}</b> to proceed.</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="space-y-3" data-testid="myroster-day-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-32 rounded-xl bg-white border border-border" />
            ))}
          </div>
        )}

        {!loading && visibleAppts.length === 0 && (
          <div className="text-center py-16 text-sm text-muted-foreground" data-testid="myroster-day-empty">
            No clients booked with you at this branch on this day.
          </div>
        )}

        {!loading && visibleAppts.length > 0 && (
          <div className="space-y-3" data-testid="myroster-day-list">
            {clientGroups.map((g) => {
              const first = g.appts[0];
              const last = g.appts[g.appts.length - 1];
              const multi = g.appts.length > 1;
              // MANDATORY (Iter-313): One Client ID per Day = ONE
              // Medical Record Button. Delegated to
              // RosterEngine.buildMedicalRecordButton so Packages,
              // Linked visits, ad-hoc solo services, and any mix
              // collapse into a single button labelled by the
              // earliest start time of the day.
              const medicalButton = buildMedicalRecordButton(g);
              const allApptsComplete = g.appts.length > 0 && g.appts.every((a) => !!a.has_medical_record);
              // NEW-guest badge — first-ever visit to the brand.
              const isNewGuest = isNewGuestGroup(g);
              // Strongest visual signal for the card. Delegated to
              // RosterEngine.deriveClientDayColor with the calendar
              // palette injected so this engine stays framework-pure.
              const cardColor = deriveClientDayColor(g, colorForAppt);
              // Iter-342 · Branch-specific accent strip. Resolves the
              // same theme keyword-mapping used by the Branch Calendar
              // (Legian → Burgundy, Ubud → Light Blue, Canggu → Sage,
              // Jimbaran → Gold, Bahrain trio + Italy). We
              // apply it as a LEFT stripe + branch chip so the
              // Personal Roster Visual Sync palette (status-based bg
              // colours) is preserved.
              const branchTheme = resolveBranchTheme(first.branch_name);
              const branchAccent = `hsl(${branchTheme.primary})`;
              return (
                <div
                  key={g.key}
                  className="rounded-xl border border-border shadow-sm overflow-hidden relative"
                  style={{
                    backgroundColor: cardColor.bg,
                    color: cardColor.text,
                    borderLeft: `6px solid ${branchAccent}`,
                  }}
                  data-testid={`myroster-day-client-${g.customer_id || g.key}`}
                  data-color={cardColor.bg}
                  data-branch-accent={branchAccent}
                >
                  <div className="p-4 bg-white text-slate-900">
                    {/* Iter-342 · Branch chip so the operator can see
                        WHICH branch this card belongs to at a glance,
                        mirroring the Branch Calendar's branch theme. */}
                    {first.branch_name && (
                      <div
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 mb-2"
                        style={{ backgroundColor: branchAccent, color: branchTheme.primaryFg ? `hsl(${branchTheme.primaryFg})` : "#fff" }}
                        data-testid={`myroster-day-branch-chip-${g.key}`}
                      >
                        <MapPin className="w-3 h-3" />
                        {first.branch_name}
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <div
                        className="flex flex-col items-center justify-center w-16 py-2 rounded-lg shrink-0 border-2"
                        style={{ backgroundColor: cardColor.bg, color: cardColor.text, borderColor: cardColor.bg }}
                        data-testid={`myroster-day-color-${g.key}`}
                      >
                        <span className="text-xs uppercase tracking-wider opacity-80">Start</span>
                        <span className="text-lg font-semibold leading-none mt-0.5">{first.start_hm}</span>
                        <span className="text-[10px] opacity-80 mt-1">to {last.end_hm}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <User className="w-3.5 h-3.5 text-slate-500" />
                          <button
                            type="button"
                            className="group flex items-center gap-1 min-w-0 font-semibold text-base text-left text-black hover:text-primary active:text-primary rounded touch-manipulation"
                            onClick={() => first.customer_id && nav(`/staff/my-roster/client?cid=${first.customer_id}`)}
                            disabled={!first.customer_id}
                            data-testid={`myroster-appt-customer-${g.key}`}
                            aria-label={`Open full medical profile for ${g.customer_name}`}
                          >
                            <span className="truncate">{g.customer_name}</span>
                            {first.customer_id && (
                              <ChevronRight className="w-4 h-4 shrink-0 text-slate-500 group-hover:text-primary group-active:text-primary transition-colors" />
                            )}
                          </button>
                          {isNewGuest && (
                            <span
                              className="text-[10px] uppercase tracking-wider font-bold text-amber-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full"
                              data-testid={`myroster-new-badge-${g.key}`}
                              title="First-ever visit across every Cocoon branch"
                            >
                              NEW
                            </span>
                          )}
                          {multi && (
                            <span className="ml-1 inline-flex items-center text-[10px] uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full" data-testid={`myroster-client-visit-count-${g.key}`}>
                              {g.appts.length} treatments
                            </span>
                          )}
                          {allApptsComplete && (
                            <span
                              className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
                              data-testid={`myroster-client-record-chip-${g.key}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Record
                            </span>
                          )}
                        </div>

                        {/* Services timeline — one row per scheduled
                            treatment so the practitioner sees the
                            client's whole visit at a glance. */}
                        <ul className="mt-2 space-y-1.5" data-testid={`myroster-client-services-${g.key}`}>
                          {g.appts.map((a) => (
                            <li key={a.id} className="flex items-start gap-2 text-sm text-black" data-testid={`myroster-service-row-${a.id}`}>
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/5 border border-primary/10 rounded-full px-2 py-0.5 shrink-0 tabular-nums">
                                <CalendarClock className="w-3 h-3" />
                                {a.start_hm}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-black font-medium">{a.service_name || "Service"}</span>
                              {a.has_medical_record && (
                                <span
                                  className="ml-auto text-emerald-600 shrink-0"
                                  title="Medical report saved"
                                  data-testid={`myroster-service-complete-${a.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>

                        {g.package_name && (
                          <div className="text-xs mt-2 inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5" data-testid={`myroster-appt-package-${g.key}`}>
                            <Sparkles className="w-3 h-3" />
                            <span className="font-medium">Package:</span>
                            <span className="truncate max-w-[16rem]">{g.package_name}</span>
                          </div>
                        )}
                        {g.assigned_team && g.assigned_team.length > 0 && (
                          <div className="text-xs mt-1 inline-flex items-center gap-1 rounded-full bg-muted text-foreground/80 px-2 py-0.5" data-testid={`myroster-appt-team-${g.key}`}>
                            <User className="w-3 h-3" />
                            <span className="font-medium">Assigned Team:</span>
                            <span className="truncate max-w-[16rem]">{g.assigned_team.join(", ")}</span>
                          </div>
                        )}
                        {/* Medical Record ownership chip — Nurse/Therapist
                            takes the lead on multi-provider bookings, but
                            every provider can still tap in to edit or add
                            to the report. */}
                        {first.report_owner_name && (
                          <div
                            className={`text-xs mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${first.is_report_owner ? "bg-primary/10 text-primary" : "bg-secondary text-foreground/80"}`}
                            data-testid={`myroster-appt-owner-${g.key}`}
                          >
                            <ClipboardEdit className="w-3 h-3" />
                            <span className="font-medium">Report owner:</span>
                            <span className="truncate max-w-[12rem]">
                              {first.is_report_owner ? "You" : first.report_owner_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quick actions — AI Summary applies to the client
                        as a whole. MANDATORY (Iter-313): One Client ID
                        per Day = ONE Medical Record button, delegated to
                        RosterEngine.buildMedicalRecordButton. Every
                        appointment (Packages, Linked, Individual) rolls
                        into this single button labelled by the earliest
                        start time of the day. */}
                    <div className="mt-4 space-y-2">
                      <Button
                        variant="outline"
                        className="h-12 w-full justify-start text-black bg-white hover:bg-slate-50"
                        onClick={() => hardStop ? toast.error("Account Locked: Please complete your missing medical reports from " + (lockInfo?.oldest_incomplete_date || "yesterday") + " to proceed.") : openBriefing(first)}
                        disabled={!first.customer_id || hardStop}
                        data-testid={`myroster-appt-ai-${g.key}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-primary" />
                        <span className="text-sm text-black">AI Summary</span>
                      </Button>
                      {medicalButton && (
                        <div data-testid={`myroster-appt-medical-buttons-${g.key}`}>
                          <Button
                            key={medicalButton.key}
                            className="h-12 w-full justify-start"
                            onClick={() => hardStop ? toast.error("Account Locked: Please complete your missing medical reports from " + (lockInfo?.oldest_incomplete_date || "yesterday") + " to proceed.") : openMedical(medicalButton.representative)}
                            disabled={hardStop}
                            data-testid={`myroster-appt-medical-${g.key}`}
                            data-linked={medicalButton.linked ? "1" : "0"}
                            data-bucket-size={medicalButton.appts.length}
                          >
                            <ClipboardEdit className="w-4 h-4 mr-2" />
                            <span className="text-sm truncate">{medicalButton.label}</span>
                            {medicalButton.linked && (
                              <Link2
                                className="w-3.5 h-3.5 ml-1.5 shrink-0 text-primary-foreground/90"
                                aria-label={`Linked session of ${medicalButton.appts.length} services`}
                                data-testid={`myroster-appt-medical-link-${g.key}`}
                              />
                            )}
                            {medicalButton.has_record && (
                              <CheckCircle2 className="w-3.5 h-3.5 ml-auto shrink-0 text-emerald-200" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {briefing && (
        <AIBriefingModal
          open={!!briefing}
          onOpenChange={(v) => { if (!v) setBriefing(null); }}
          customerId={briefing.customerId}
          customerName={briefing.customerName}
          serviceId={briefing.serviceId}
          serviceName={briefing.serviceName}
          appointmentId={briefing.appointmentId}
        />
      )}

      {medical && (
        <MedicalRecordDialog
          open={!!medical}
          onOpenChange={(v) => { if (!v) { setMedical(null); load(); loadLock(); } }}
          appt={medical}
        />
      )}
    </div>
  );
}
